import React, { useState } from 'react';
import { User, Phone, Home, Shield, Signal, Settings, Trash2 } from 'lucide-react';

interface UserProfile {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  primaryPhone: string;
  email: string;
}

interface Caretaker {
  id: string;
  name: string;
  relationship: string;
  phone: string;
  email: string;
  isPrimary: boolean;
}

interface ConnectedDevice {
  id: string;
  name: string;
  type: string;
  status: 'online' | 'offline' | 'updating';
  lastSeen: Date;
  softwareVersion: string;
  firmwareVersion: string;
  signalStrength: number;
  sensors: {
    total: number;
    active: number;
  };
}

const UserSettings: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile>({
    firstName: 'Emma',
    lastName: 'Martinez',
    dateOfBirth: '1945-06-15',
    address: '123 Maple Street',
    city: 'Milwaukee',
    state: 'WI',
    zipCode: '53202',
    primaryPhone: '(414) 555-0123',
    email: 'emma.martinez@example.com'
  });

  const [caretakers, setCaretakers] = useState<Caretaker[]>([
    {
      id: '1',
      name: 'John Martinez',
      relationship: 'Son',
      phone: '(414) 555-0124',
      email: 'john.martinez@example.com',
      isPrimary: true
    },
    {
      id: '2',
      name: 'Sarah Johnson',
      relationship: 'Nurse',
      phone: '(414) 555-0125',
      email: 'sarah.j@careprovider.com',
      isPrimary: false
    }
  ]);

  const [connectedDevices, setConnectedDevices] = useState<ConnectedDevice[]>([
    {
      id: 'SB-001',
      name: 'Scanalytics Basestation',
      type: 'Floor Sensor Hub',
      status: 'online',
      lastSeen: new Date(),
      softwareVersion: '2.1.0',
      firmwareVersion: '1.4.2',
      signalStrength: 85,
      sensors: {
        total: 64,
        active: 64
      }
    }
  ]);

  const handleProfileChange = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddCaretaker = () => {
    const newCaretaker: Caretaker = {
      id: Date.now().toString(),
      name: '',
      relationship: '',
      phone: '',
      email: '',
      isPrimary: false
    };
    setCaretakers(prev => [...prev, newCaretaker]);
  };

  const handleCaretakerChange = (id: string, field: keyof Caretaker, value: string | boolean) => {
    setCaretakers(prev => prev.map(caretaker => 
      caretaker.id === id ? { ...caretaker, [field]: value } : caretaker
    ));
  };

  const handleRemoveCaretaker = (id: string) => {
    setCaretakers(prev => prev.filter(caretaker => caretaker.id !== id));
  };

  const getSignalStrengthColor = (strength: number) => {
    if (strength >= 80) return 'text-green-500';
    if (strength >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Settings</h2>
      
      <div className="space-y-6">
        {/* User Profile Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4 flex items-center">
            <User className="mr-2 text-blue-500" />
            Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={profile.firstName}
                onChange={(e) => handleProfileChange('firstName', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={profile.lastName}
                onChange={(e) => handleProfileChange('lastName', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input
                type="date"
                value={profile.dateOfBirth}
                onChange={(e) => handleProfileChange('dateOfBirth', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={profile.primaryPhone}
                onChange={(e) => handleProfileChange('primaryPhone', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => handleProfileChange('email', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={profile.address}
                onChange={(e) => handleProfileChange('address', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={profile.city}
                onChange={(e) => handleProfileChange('city', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={profile.state}
                  onChange={(e) => handleProfileChange('state', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                <input
                  type="text"
                  value={profile.zipCode}
                  onChange={(e) => handleProfileChange('zipCode', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Caretakers Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold flex items-center">
              <Shield className="mr-2 text-green-500" />
              Caretakers
            </h3>
            <button
              onClick={handleAddCaretaker}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Add Caretaker
            </button>
          </div>
          <div className="space-y-4">
            {caretakers.map(caretaker => (
              <div key={caretaker.id} className="border rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={caretaker.name}
                      onChange={(e) => handleCaretakerChange(caretaker.id, 'name', e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                    <input
                      type="text"
                      value={caretaker.relationship}
                      onChange={(e) => handleCaretakerChange(caretaker.id, 'relationship', e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={caretaker.phone}
                      onChange={(e) => handleCaretakerChange(caretaker.id, 'phone', e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={caretaker.email}
                      onChange={(e) => handleCaretakerChange(caretaker.id, 'email', e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={caretaker.isPrimary}
                      onChange={(e) => handleCaretakerChange(caretaker.id, 'isPrimary', e.target.checked)}
                      className="rounded text-blue-500 focus:ring-blue-500 mr-2"
                    />
                    <span className="text-sm text-gray-700">Primary Contact</span>
                  </label>
                  <button
                    onClick={() => handleRemoveCaretaker(caretaker.id)}
                    className="text-red-500 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Connected Devices Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4 flex items-center">
            <Settings className="mr-2 text-purple-500" />
            Connected Devices
          </h3>
          <div className="space-y-4">
            {connectedDevices.map(device => (
              <div key={device.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="font-medium">{device.name}</h4>
                    <p className="text-sm text-gray-600">{device.type}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      device.status === 'online' ? 'bg-green-500' : 'bg-red-500'
                    }`}></span>
                    <span className="text-sm text-gray-600">{device.status}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Software Version</p>
                    <p className="font-medium">{device.softwareVersion}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Firmware Version</p>
                    <p className="font-medium">{device.firmwareVersion}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Signal Strength</p>
                    <p className="font-medium flex items-center">
                      <Signal className={getSignalStrengthColor(device.signalStrength)} size={16} />
                      <span className="ml-1">{device.signalStrength}%</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Active Sensors</p>
                    <p className="font-medium">{device.sensors.active} / {device.sensors.total}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={() => {
              // In a real app, this would save all settings
              alert('Settings saved successfully!');
            }}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserSettings; 